import { fromJS, Map } from 'immutable';
import actionTypes from 'constants/checkoutDataConstants';
import { saveState, preloadState } from 'lib/formStorage';

export const initialState = fromJS({
  market: null,
  market_public_name: null,
  pricing_tier_level: null,
  currency: 'GBP',
  event_quantity: 1,
  package_type: 'single',
  package_volume: null,
  current_event: null,
  eventImagePolling: {
    isPolling: false,
    tries: 0,
  },
  package_template_set: {
    single_available: true,
    subscription_available: true,
    free_available: false,
    bank_payment_available: false,
  },
  isPriceLoading: false,
  isSinglePriceLoading: true,
  isSubscriptionPriceLoading: true,
  currentPrice: 0,
  current_testimonials: null,
  on_success_redirect_to: null,
  partner_name: null,
  partner_exports: null,
  api_key: null,
  available_currencies: null,
  is_upgrading: false,
  credits_left: null,
  package_id: null,
  prepay: false,
  snap_sell_package_id: null,
  featuring_publisher: null,
  valid_for_origin_publisher: null,
  singlePrice: 0,
  subscriptionPrice: 0,
  form: {
    isLoading: false,
    errors: { global: '' },
  },
  invoice_id: null,
  is_email_campaign_enterprise: null,
  email_campaign_id: null,
  email_campaign_audience: null,
  email_campaign_location: null,
  publisher_id: null,
  promo_content: null,
  business: null,
  print_eligible: true,
  ...preloadState('checkout'),
});

function option(changeActionType, state, action) {
  switch(action.type) {
    case changeActionType:
      return action.payload;
    default:
      return state;
  }
}

function price(state, action) {
  switch(action.type) {
    case actionTypes.FETCH_PRICE_SUCCESS:
      return parseFloat(action.payload.amount).toFixed(2);
    case actionTypes.FETCH_PRICE_FAILURE:
      return NaN;
    default:
      return state;
  }
}

function loading(state, action) {
  switch(action.type) {
    case actionTypes.FETCH_PRICE_REQUEST:
      return true;
    case actionTypes.FETCH_PRICE_SUCCESS:
    case actionTypes.FETCH_PRICE_FAILURE:
      return false;
    default:
      return state;
  }
}

function priceSingle(state, action) {
  switch(action.type) {
    case actionTypes.FETCH_SINGLE_PRICE_SUCCESS:
      return parseInt(action.payload.amount);
    case actionTypes.FETCH_SINGLE_PRICE_FAILURE:
      return NaN;
    default:
      return state;
  }
}

function loadingSinglePrice(state, action) {
  switch(action.type) {
    case actionTypes.FETCH_SINGLE_PRICE_REQUEST:
      return true;
    case actionTypes.FETCH_SINGLE_PRICE_SUCCESS:
    case actionTypes.FETCH_SINGLE_PRICE_FAILURE:
      return false;
    default:
      return state;
  }
}

function priceSubscription(state, action) {
  switch(action.type) {
    case actionTypes.FETCH_SUBSCRIPTION_PRICE_SUCCESS:
      return parseInt(action.payload.amount);
    case actionTypes.FETCH_SUBSCRIPTION_PRICE_FAILURE:
      return NaN;
    default:
      return state;
  }
}

function loadingSubscriptionPrice(state, action) {
  switch(action.type) {
    case actionTypes.FETCH_SUBSCRIPTION_PRICE_REQUEST:
      return true;
    case actionTypes.FETCH_SUBSCRIPTION_PRICE_SUCCESS:
    case actionTypes.FETCH_SUBSCRIPTION_PRICE_FAILURE:
      return false;
    default:
      return state;
  }
}

// based on https://stripe.com/docs/api#errors
function handleStripeErrors(state, payload) {
  const errorToFieldMapping = {
    incomplete_number: 'card_number',
    invalid_number: 'card_number',
    incomplete_expiry: 'card_expiry_date',
    invalid_expiry_month: 'card_expiry_date',
    invalid_expiry_year: 'card_expiry_date',
    incomplete_cvc: 'card_cvc',
    invalid_cvc: 'card_cvc',
    incorrect_number: 'card_number',
    expired_card: 'global',
    incorrect_cvc: 'card_cvc',
    incorrect_zip: 'zip_code',
    card_declined: 'global',
    processing_error: 'global',
  };

  if (errorToFieldMapping[payload.code]) {
    return state.set('errors', Map({ [errorToFieldMapping[payload.code]]: payload.message }));
  } else {
    return state.set('errors', Map({ global: `Unknown error has occured - ${payload.message}` }));
  }
}

function handleFormErrors(state, payload) {
  if (payload !== undefined) {
    if (!payload.details) {
      return state.set('errors', Map({ global: payload.message }));
    }

    if (payload.details.code) {
      return handleStripeErrors(state, payload.details);
    }

    return state.set('errors', Map(payload.details));
  }

  return state;
}

function form(state, action) {
  switch(action.type) {
    case actionTypes.CREATE_STRIPE_CARD_REQUEST:
    case actionTypes.CREATE_ORDER_REQUEST:
      return state.set('isLoading', true);
    case actionTypes.CREATE_STRIPE_CARD_FAILURE:
      return handleStripeErrors(state.set('isLoading', false), action.payload);
    case actionTypes.CREATE_ORDER_SUCCESS:
      return state.set('isLoading', action.payload);
    case actionTypes.CREATE_ORDER_FAILURE:
      return handleFormErrors(state.set('isLoading', false), action.payload);
    default:
      return state;
  }
}

function eventImagePolling(state, action) {
  switch(action.type) {
    case actionTypes.FETCH_EVENT_IMAGE_REQUEST:
      return state.set('isPolling', true).set('tries', state.get('tries') + 1);
    case actionTypes.FETCH_EVENT_IMAGE_SUCCESS:
    case actionTypes.FETCH_EVENT_IMAGE_FAILURE:
      return state.set('isPolling', false).set('tries', 0);
    default:
      return state;
  }
}

function currentEvent(state, action) {
  switch(action.type) {
    case actionTypes.FETCH_EVENT_IMAGE_SUCCESS:
      return fromJS(action.payload);
    default:
      return state;
  }
}

function snapSellPackageId(state, action) {
  switch(action.type) {
    case actionTypes.SET_SNAP_SELL_PACKAGE_ID:
      return parseInt(action.payload, 10);
    default:
      return state;
  }
}

function isUpgrading(state, action) {
  switch(action.type) {
    case actionTypes.IS_UPGRADING_CHANGED:
      return action.is_upgrading;
    default:
      return state;
  }
}

function printEligible(state, action) {
  switch(action.type) {
    case actionTypes.PRINT_ELIGIBILITY_TOGGLED:
      return !state;
    default:
      return state;
  }
}

const checkoutDataReducer = (state = initialState, action) => {
  const newState = Map({
    market: state.get('market'),
    market_public_name: state.get('market_public_name'),
    pricing_tier_level: state.get('pricing_tier_level'),
    featuring_publisher: state.get('featuring_publisher'),
    valid_for_origin_publisher: state.get('valid_for_origin_publisher'),
    currency: option(actionTypes.PACKAGE_CURRENCY_CHANGED, state.get('currency'), action),
    event_quantity: option(actionTypes.PACKAGE_EVENT_QUANTITY_CHANGED, state.get('event_quantity'), action),
    package_type: option(actionTypes.PACKAGE_TYPE_CHANGED, state.get('package_type'), action),
    current_event: currentEvent(state.get('current_event'), action),
    currentPrice: price(state.get('currentPrice'), action),
    singlePrice: priceSingle(state.get('singlePrice'), action),
    subscriptionPrice: priceSubscription(state.get('subscriptionPrice'), action),
    isPriceLoading: loading(state.get('isPriceLoading'), action),
    isSinglePriceLoading: loadingSinglePrice(state.get('isSinglePriceLoading'), action),
    isSubscriptionPriceLoading: loadingSubscriptionPrice(state.get('isSubscriptionPriceLoading'), action),
    current_testimonials: state.get('current_testimonials'),
    form: form(state.get('form'), action),
    on_success_redirect_to: state.get('on_success_redirect_to'),
    partner_name: state.get('partner_name'),
    partner_exports: state.get('partner_exports'),
    api_key: state.get('api_key'),
    available_currencies: state.get('available_currencies') || ['USD', 'GBP', 'EUR'],
    is_upgrading: isUpgrading(state.get('is_upgrading'), action),
    print_eligible: printEligible(state.get('print_eligible'), action),
    credits_left: state.get('credits_left'),
    package_id: state.get('package_id'),
    prepay: state.get('prepay'),
    package_volume: state.get('package_volume'),
    eventImagePolling: eventImagePolling(state.get('eventImagePolling'), action),
    invoice_id: state.get('invoice_id'),
    email_campaign_id: state.get('email_campaign_id'),
    is_email_campaign_enterprise: state.get('is_email_campaign_enterprise'),
    email_campaign_audience: state.get('email_campaign_audience'),
    email_campaign_location: state.get('email_campaign_location'),
    email_campaign_broadcast_time: state.get('email_campaign_broadcast_time'),
    publisher_id: state.get('publisher_id'),
    promo_content: state.get('promo_content'),
    package_template_set: state.get('package_template_set'),
    snap_sell_package_id: snapSellPackageId(state.get('snap_sell_package_id'), action),
    business: state.get('business')
  });

  switch(action.type) {
    case actionTypes.SET_SNAP_SELL_PACKAGE_ID:
      saveState({ checkout: newState });
      return newState;
    case actionTypes.SETUP_CHECKOUT:
      saveState({ checkout: action.payload });
      const newCurrentEvent = fromJS(action.payload).get('current_event');
      return newState.mergeDeep(fromJS(action.payload)).set('current_event', newCurrentEvent);
    default:
      return newState;
  }
};

export default checkoutDataReducer;



// WEBPACK FOOTER //
// app/client/bundles/EventSubmission/reducers/checkoutDataReducer.js